import React, { useState } from "react";
import { X, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { formatBotMessage } from "./utils/formatMessage";
import { isBatchQuestion } from "./utils/batchAnswer";
import { QuestionBatchBody, useBatchPicks } from "./QuestionBatch";

// The followup-suggestions menu carries this exact question text from the
// server (faststage.clarify_questions). It is NOT a diagnostic question: it
// renders inline in the transcript as suggestion bubbles rather than in this
// card — see CopilotPanel's "followups" message branch. Exported so every site
// that has to tell the two apart agrees on one string.
export const FOLLOWUPS_MARKER = "Helpful followups";
export const TYPED_WITH_PICKS_LABEL = "Picks and typed answer will be sent";

/**
 * QuestionCard — clarifying-question card shown above the composer.
 *  - inputType "single": vertical option buttons, answers immediately
 *  - inputType "multi": checkboxes + submit button
 *  - inputType "text" (or no options): question text only — the actual
 *    textarea lives in the parent composer, this just renders the prompt
 *  - pills: Diagnostic Agent cards (tse:question with a diagnosis_id) show the
 *    options as compact wrapping pills, several per line, like the POC chat v4
 *    question card (learning_brain_poc chat4_qcard.js / chat4.css .v4-qcard-option).
 *    Legacy cards keep the one-option-per-row column.
 *  - questions: a Diagnostic Agent batch (tse:question `questions`, 2..5) shows
 *    every question together (QuestionBatchBody) under a quiet "Questions" label
 *    with an "answered/total" count. The card has no Send of its own: the
 *    composer's send button sends it (owner 23 Oct). `batchState` (useBatchPicks)
 *    comes from the composer owner; without it the card keeps its own picks.
 *    `typedOverride` swaps the count for TYPED_WITH_PICKS_LABEL (the picks go with the typed text).
 *    Without `questions` the card is unchanged.
 */
const QuestionCard = ({ question, options = [], inputType = "text", onAnswer, onDismiss, pills = false, questions = null, batchState = null, disabled = false, typedOverride = false }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const batch = isBatchQuestion({ questions }) ? questions : null;
  const ownBatch = useBatchPicks(batch);
  const picks = batchState || ownBatch;

  const toggleOption = (opt) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(opt)) next.delete(opt); else next.add(opt);
      return next;
    });
  };

  return (
    <div className="d5-question-card">
      <div className={`d5-question-header${batch ? " d5-question-header-batch" : ""}`}>
        {!collapsed && question === FOLLOWUPS_MARKER && (
          <div className="d5-question-text" title="Suggested followups">
            <Sparkles size={16} strokeWidth={2} />
          </div>
        )}
        {!collapsed && batch && <div className="d5-qbatch-label">Questions</div>}
        {!collapsed && !batch && question && question !== FOLLOWUPS_MARKER && (
          <div className="d5-question-text" dangerouslySetInnerHTML={{ __html: formatBotMessage(question) }} />
        )}
        {collapsed && (
          <span className="d5-question-collapsed-label">
            {batch ? `${batch.length} questions pending — expand to answer` : "Question pending — expand to answer"}
          </span>
        )}
        {!collapsed && batch && (
          <span className={`d5-qbatch-count${picks.complete && !typedOverride ? " d5-qbatch-count-done" : ""}`} aria-live="polite">
            {typedOverride ? TYPED_WITH_PICKS_LABEL : `${picks.answered}/${picks.total}`}
          </span>
        )}
        <div className="d5-question-controls">
          {/* Arrow points the way the card moves, matching Dashboard2: the card
              sits above the composer, so expanding grows it upward and
              collapsing shrinks it downward. These were the wrong way round. */}
          <button type="button" className="d5-icon-btn" title={collapsed ? "Expand" : "Collapse"} onClick={() => setCollapsed((v) => !v)}>
            {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button type="button" className="d5-icon-btn" title="Dismiss" onClick={onDismiss}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Hidden, not unmounted, while collapsed: picks made so far survive a collapse. */}
      {batch && (
        <div className="d5-question-body" hidden={collapsed}>
          <QuestionBatchBody questions={batch} picks={picks.picks} onPick={picks.pick} disabled={disabled} />
        </div>
      )}

      {!collapsed && !batch && (
        <div className="d5-question-body">
          {inputType === "single" && options.length > 0 && (
            <div className={pills ? "d5-question-options-pills" : "d5-question-options-col"}>
              {options.map((opt, i) => (
                <button key={i} type="button" className={pills ? "d5-question-option d5-question-pill" : "d5-question-option"} disabled={disabled} onClick={() => onAnswer?.(opt)}>
                  <span dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} />
                </button>
              ))}
            </div>
          )}

          {inputType === "multi" && options.length > 0 && (
            <div className="d5-question-options-col">
              {options.map((opt, i) => {
                const checked = selected.has(opt);
                return (
                  <button
                    key={i}
                    type="button"
                    className={`d5-question-option d5-question-option-checkbox ${checked ? "d5-question-option-checked" : ""}`}
                    disabled={disabled}
                    onClick={() => toggleOption(opt)}
                  >
                    <span className={`d5-checkbox ${checked ? "d5-checkbox-checked" : ""}`} />
                    <span dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} />
                  </button>
                );
              })}
              <button
                type="button"
                className="d5-question-submit"
                disabled={disabled || selected.size === 0}
                onClick={() => onAnswer?.(JSON.stringify([...selected]))}
              >
                Submit ({selected.size} selected)
              </button>
            </div>
          )}

          {(inputType === "text" || options.length === 0) && (
            <p className="d5-question-hint">Type your answer in the box below and press Enter.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
